export interface CreateNoteByAdmin {
  note: string;
  lostId: string;
}

export interface UpdateNoteByAdmin {
  id: string;
  note: string;
  lostId: string;
}

export interface FoundUpdateNoteByAdmin {
  id: string;
  note: string;
  foundId: string;
}

export interface PaymentLog {
  id: string;
  lostId: string;
  trackingId: string;
  name: string;
  paymentGateway: number;
  status: number;
  totalPayableAmount: number;
  createdAt: string;
  itemNumber?: string;
  // Payment details
  paymentIntentId?: string;
  cardBrand?: string;
  cardLastFour?: string;
  payerEmail?: string;
  payerName?: string;
  ip?: string;
  browser?: string;
  // Webhook fields
  webhookEventId?: string;
  captureId?: string;
  currency?: string;
  paypalOrderStatus?: string;
  paymentMethod?: string;
  webhookReceivedAt?: string;
}

export interface EmailLog {
  id: string;
  subject: string;
  body: string;
  admin: string | null;
  from: string;
  to: string;
  status: string;
  sendOn: string;
  triggerBy?: string;
  trackingId?: string;
  itemNumber?: string;
  lostStatus?: string;
  foundStatus?: string;
}

export interface LostItemLogsData {
  id: string;
  description: string;
  note: string | null;
  type: string;
  trackingId: string;
  itemNumber: string | null;
  createdAt: string;
  lostId: string;
  foundId: string | null;
  lostDate: string;
  adminName: string | null;
  userName: string | null;
  userEmail: string | null;
  emailLog: EmailLog | null;
  foundStatus: number;
  lostStatus: number;
}

export enum LostItemActivityLogType {
  StickyAdded = 1,
  StickyUpdated = 2,
  StickyDone = 3,
  SendLostRequest = 4,
  ClaimStatusChange = 5,
  ClaimUpdated = 6,
  FoundItemCreated = 7,
  FoundItemUpdated = 8,
  FoundItemStatusChanged = 9,
  EmailSent = 10,
  Note = 11,
  SendFoundRequest = 12,
  SystemMovedLostItem = 13,
  ToggleUrgent = 14,
  ToggleEmailCommunication = 15,
  LostDateUpdated = 16,
  ClaimUpdateRequestApproved = 17,
  ClaimUpdateRequestRejected = 18,
  ClaimDeleteRequestApproved = 19,
  ClaimDeleteRequestRejected = 20,
  ClaimPaymentSuccess = 21,
  ClaimPaymentFailed = 22,
  UpdateTrackingNumber = 23,
  SendPaymentRequest = 24,
  ClaimPaymentRefunded = 25,
  ClaimUpdateBillingInfoRequestApproved = 26,
  ClaimUpdateBillingInfoRequestRejected = 27,
  UploadShippingLabel = 28,
  UploadAdditionalDoc = 29,
  UploadShippingProof = 31,
  ClaimUpdateRequestReceived = 32,
  ClaimDeleteRequestReceived = 33,
  BillingInfoUpdateRequestReceived = 34,
  UseCredits = 35,
  CancelShippingLabel = 36,
  CancelShippingLabelFailed = 37,
  CreateAuction = 38,
  UpdateAuction = 39,
  AuctionStatusChange = 40,
  AuctionPaymentSuccess = 41,
  AuctionPaymentFailed = 42,
  AuctionPaymentRefunded = 43,
  AuctionBillingInfoUpdateRequestReceived = 44,
  UpdateShippingLabelCourier = 45,
  UpdateShippingOption = 46,
  SendPaymentReminder = 47,
  DeleteItemAnswers = 48,
}

export const DefaultUserName = "System";

// Backend Activity Log enum (matches backend/src/constants/application.const.ts)
export enum BackendActivityLogType {
  StickyAdded = 1,
  StickyUpdated = 2,
  StickyDone = 3,
  SendLostRequest = 4,
  ClaimStatusChange = 5,
  ClaimUpdated = 6,
  EmailSent = 7,
  NoteAdded = 8,
  SystemMovedLostItem = 9,
  ToggleUrgent = 10,
  ToggleEmailCommunication = 11,
  LostDateUpdated = 12,
  ClaimUpdateRequestApproved = 13,
  ClaimUpdateRequestRejected = 14,
  ClaimDeleteRequestApproved = 15,
  ClaimDeleteRequestRejected = 16,
  ClaimPaymentSuccess = 17,
  ClaimPaymentFailed = 18,
  SendPaymentRequest = 19,
  ClaimPaymentRefunded = 20,
  ClaimUpdateBillingInfoRequestApproved = 21,
  ClaimUpdateBillingInfoRequestRejected = 22,
  ItemDonated = 23,
  ClaimUpdateRequestReceived = 24,
  ClaimDeleteRequestReceived = 25,
  BillingInfoUpdateRequestReceived = 26,
  UploadShippingProof = 27,
  SendPaymentReminder = 28,
  UserAccountCreationEmail = 29
}

// Text labels for backend activity types
export const BackendActivityLogText = {
  [BackendActivityLogType.StickyAdded]: "Sticky added",
  [BackendActivityLogType.StickyUpdated]: "Sticky updated",
  [BackendActivityLogType.StickyDone]: "Sticky done",
  [BackendActivityLogType.SendLostRequest]: "Lost request sent",
  [BackendActivityLogType.ClaimStatusChange]: "Claim status changed",
  [BackendActivityLogType.ClaimUpdated]: "Claim updated",
  [BackendActivityLogType.EmailSent]: "Email sent",
  [BackendActivityLogType.NoteAdded]: "Note added",
  [BackendActivityLogType.SystemMovedLostItem]: "System moved lost item",
  [BackendActivityLogType.ToggleUrgent]: "Urgent",
  [BackendActivityLogType.ToggleEmailCommunication]: "Email communication toggled",
  [BackendActivityLogType.LostDateUpdated]: "Lost date updated",
  [BackendActivityLogType.ClaimUpdateRequestApproved]: "Claim update request approved",
  [BackendActivityLogType.ClaimUpdateRequestRejected]: "Claim update request rejected",
  [BackendActivityLogType.ClaimDeleteRequestApproved]: "Claim delete request approved",
  [BackendActivityLogType.ClaimDeleteRequestRejected]: "Claim delete request rejected",
  [BackendActivityLogType.ClaimPaymentSuccess]: "Claim payment successful",
  [BackendActivityLogType.ClaimPaymentFailed]: "Claim payment failed",
  [BackendActivityLogType.SendPaymentRequest]: "Payment request sent",
  [BackendActivityLogType.ClaimPaymentRefunded]: "Claim payment refunded",
  [BackendActivityLogType.ClaimUpdateBillingInfoRequestApproved]: "Billing info update approved",
  [BackendActivityLogType.ClaimUpdateBillingInfoRequestRejected]: "Billing info update rejected",
  [BackendActivityLogType.ItemDonated]: "Item donated",
  [BackendActivityLogType.ClaimUpdateRequestReceived]: "Claim update request received",
  [BackendActivityLogType.ClaimDeleteRequestReceived]: "Claim delete request received",
  [BackendActivityLogType.BillingInfoUpdateRequestReceived]: "Billing info update request received",
  [BackendActivityLogType.UploadShippingProof]: "Shipping proof uploaded",
  [BackendActivityLogType.SendPaymentReminder]: "Payment reminder sent",
  [BackendActivityLogType.UserAccountCreationEmail]: "User account creation email sent"
};

export const LostItemActivityLogText = {
  [LostItemActivityLogType.StickyAdded]: "Sticky added",
  [LostItemActivityLogType.StickyUpdated]: "Sticky updated",
  [LostItemActivityLogType.StickyDone]: "Sticky done",
  [LostItemActivityLogType.SendLostRequest]: "Lost request sent",
  [LostItemActivityLogType.ClaimStatusChange]: "Claim status changed",
  [LostItemActivityLogType.ClaimUpdated]: "Claim updated",
  [LostItemActivityLogType.FoundItemCreated]: "Found item created",
  [LostItemActivityLogType.FoundItemUpdated]: "Found item updated",
  [LostItemActivityLogType.FoundItemStatusChanged]: "Found item status changed",
  [LostItemActivityLogType.EmailSent]: "Email sent",
  [LostItemActivityLogType.Note]: "Note",
  [LostItemActivityLogType.SendFoundRequest]: "Found request sent",
  [LostItemActivityLogType.SystemMovedLostItem]: "System moved lost item",
  [LostItemActivityLogType.ToggleUrgent]: "Urgent",
  [LostItemActivityLogType.ToggleEmailCommunication]: "Email communication toggled",
  [LostItemActivityLogType.LostDateUpdated]: "Lost date updated",
  [LostItemActivityLogType.ClaimUpdateRequestApproved]: "Claim update request approved",
  [LostItemActivityLogType.ClaimUpdateRequestRejected]: "Claim update request rejected",
  [LostItemActivityLogType.ClaimDeleteRequestApproved]: "Claim delete request approved",
  [LostItemActivityLogType.ClaimDeleteRequestRejected]: "Claim delete request rejected",
  [LostItemActivityLogType.ClaimPaymentSuccess]: "Claim payment successful",
  [LostItemActivityLogType.ClaimPaymentFailed]: "Claim payment failed",
  [LostItemActivityLogType.UpdateTrackingNumber]: "Tracking number updated",
  [LostItemActivityLogType.SendPaymentRequest]: "Payment request sent",
  [LostItemActivityLogType.ClaimPaymentRefunded]: "Claim payment refunded",
  [LostItemActivityLogType.ClaimUpdateBillingInfoRequestApproved]: "Billing info update approved",
  [LostItemActivityLogType.ClaimUpdateBillingInfoRequestRejected]: "Billing info update rejected",
  [LostItemActivityLogType.UploadShippingLabel]: "Shipping label uploaded",
  [LostItemActivityLogType.UploadAdditionalDoc]: "Additional document uploaded",
  [LostItemActivityLogType.UploadShippingProof]: "Shipping proof uploaded",
  [LostItemActivityLogType.ClaimUpdateRequestReceived]: "Claim update request received",
  [LostItemActivityLogType.ClaimDeleteRequestReceived]: "Claim delete request received",
  [LostItemActivityLogType.BillingInfoUpdateRequestReceived]: "Billing info update request received",
  [LostItemActivityLogType.UseCredits]: "Credit used",
  [LostItemActivityLogType.CancelShippingLabel]: "Shipping label cancelled",
  [LostItemActivityLogType.CancelShippingLabelFailed]: "Shipping label cancellation failed",
  [LostItemActivityLogType.CreateAuction]: "Auction created",
  [LostItemActivityLogType.UpdateAuction]: "Auction updated",
  [LostItemActivityLogType.AuctionStatusChange]: "Auction status changed",
  [LostItemActivityLogType.AuctionPaymentSuccess]: "Auction payment successful",
  [LostItemActivityLogType.AuctionPaymentFailed]: "Auction payment failed",
  [LostItemActivityLogType.AuctionPaymentRefunded]: "Auction payment refunded",
  [LostItemActivityLogType.AuctionBillingInfoUpdateRequestReceived]: "Auction billing info update request received",
  [LostItemActivityLogType.UpdateShippingLabelCourier]: "Shipping label courier updated",
  [LostItemActivityLogType.UpdateShippingOption]: "Shipping option updated",
  [LostItemActivityLogType.SendPaymentReminder]: "Payment reminder sent",
  [LostItemActivityLogType.DeleteItemAnswers]: "Delete item answers",
};

// Helper function to get activity log text using backend type mapping
export const getActivityLogText = (type: number | LostItemActivityLogType | BackendActivityLogType): string => {
  // Use backend enum for actual activity log values
  return BackendActivityLogText[type as BackendActivityLogType] ?? "-";
};

export const ActivityLogTypeValue = "activityLog.type";
export const ActivityLogOptions = [
  { value: "activityLog.trackingId", label: "Claim Number" },
  { value: ActivityLogTypeValue, label: "Activity Type" },
  { value: "activityLog.doneBy", label: "Done By" },
];

export const CustomersOptions = [
  { value: "customer.trackingId", label: "Claim Number" },
  { value: "customer.name", label: "Name" },
  { value: "customer.email", label: "Email" },
];

export const EmailLogsStatus = {
  Sent: 'Sent',
  Error: 'Error',
  Pending: 'Pending'
}
